import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Users, GraduationCap, BookOpen, Award,
  ClipboardList, Grid, Megaphone, AlertTriangle, CalendarDays, Brain,
  LogOut, Bell, Menu, X, Search,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard',         path: '/teacher/dashboard' },
  { icon: ClipboardList,   label: 'Assignments',        path: '/teacher/assignments' },
  { icon: Award,           label: 'Enter Results',      path: '/teacher/results' },
  { icon: Users,           label: 'My Class',           path: '/teacher/my-class' },
  { icon: GraduationCap,   label: 'My Students',        path: '/teacher/my-students' },
  { icon: BookOpen,        label: 'Lesson Notes',       path: '/teacher/lessons', notif: true },
  { icon: Grid,            label: 'Timetable',          path: '/teacher/timetable' },
  { icon: Megaphone,       label: 'Announcements',      path: '/teacher/announcements' },
  { icon: AlertTriangle,   label: 'Weak Topic Alerts',  path: '/teacher/weak-topic-alerts' },
  { icon: CalendarDays,    label: 'Events & Calendar',  path: '/teacher/events' },
  { icon: Brain,           label: 'AI Assistant',       path: '/teacher/ai' },
];

export default function TeacherLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadComments, setUnreadComments] = useState(0);
  const { user, school, logout } = useAuth();
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchCount = () =>
      api.get('/lesson-notes/notification-count')
        .then(r => setUnreadComments(r.data?.unread || 0))
        .catch(() => {});
    fetchCount();
    intervalRef.current = setInterval(fetchCount, 60000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
    : 'T';
  const fullName  = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'Teacher';
  const shortName = (user?.first_name || fullName).slice(0, 18);

  return (
    <div className="flex h-screen overflow-hidden" data-testid="teacher-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 lg:hidden"
          style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── SIDEBAR ────────────────────────────────────────────────── */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40 lg:z-auto
          w-[240px] flex flex-col flex-shrink-0
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{
          backgroundColor: '#0B0F19',
          borderRight: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        {/* Logo */}
        <div
          className="h-[64px] flex items-center px-5 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#2563EB' }}
            >
              <BookOpen className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-white text-[13px] leading-tight tracking-tight">EDUVA</p>
              <p className="text-[10px] leading-tight truncate" style={{ color: '#475569' }}>
                {school?.name || 'School OS'}
              </p>
            </div>
          </div>
          <button
            className="ml-auto lg:hidden p-1 rounded-lg"
            style={{ color: '#475569' }}
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-[2px]">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="flex items-center gap-3 py-[9px] pr-3 rounded-xl text-[13px] font-medium"
              style={({ isActive }) => ({
                paddingLeft: isActive ? '10px' : '12px',
                backgroundColor: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
                color: isActive ? '#FFFFFF' : '#64748B',
                borderLeft: isActive ? '2px solid #3B82F6' : '2px solid transparent',
                transition: 'background-color 0.15s ease, color 0.15s ease',
              })}
              onMouseEnter={e => {
                if (!e.currentTarget.style.backgroundColor.includes('0.08')) {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)';
                  e.currentTarget.style.color = '#E2E8F0';
                }
              }}
              onMouseLeave={e => {
                if (!e.currentTarget.style.backgroundColor.includes('0.08')) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#64748B';
                }
              }}
            >
              <item.icon className="w-[16px] h-[16px] flex-shrink-0" strokeWidth={1.75} />
              <span className="flex-1 truncate">{item.label}</span>
              {item.notif && unreadComments > 0 && (
                <span
                  className="flex-shrink-0 min-w-[18px] h-[18px] rounded-full flex items-center justify-center font-bold text-white px-1"
                  style={{ backgroundColor: '#EF4444', fontSize: '10px' }}
                  data-testid="lesson-notes-notification-dot"
                >
                  {unreadComments > 9 ? '9+' : unreadComments}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Teacher footer */}
        <div
          className="p-4 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
              style={{ backgroundColor: '#2563EB' }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-white truncate leading-tight">{shortName}</p>
              <p className="text-[11px]" style={{ color: '#475569' }}>Teacher</p>
            </div>
            <button
              onClick={logout}
              className="p-1.5 rounded-lg flex-shrink-0 transition-colors"
              style={{ color: '#475569' }}
              title="Logout"
              data-testid="logout-btn"
              onMouseEnter={e => { e.currentTarget.style.color = '#FFFFFF'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── MAIN ──────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header
          className="h-[64px] flex items-center justify-between px-5 flex-shrink-0 z-20"
          style={{
            backgroundColor: 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid #E2E8F0',
          }}
        >
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
              data-testid="sidebar-toggle"
            >
              <Menu className="w-5 h-5" strokeWidth={1.75} />
            </button>

            <div className="hidden md:flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 w-56 transition-colors focus-within:border-blue-300 focus-within:bg-white">
              <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" strokeWidth={1.75} />
              <input
                placeholder="Search..."
                className="bg-transparent text-xs text-slate-700 placeholder-slate-400 outline-none w-full"
                data-testid="header-search"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="relative p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="notifications-btn"
            >
              <Bell className="w-[18px] h-[18px]" strokeWidth={1.75} />
              <span
                className="absolute top-[7px] right-[7px] w-2 h-2 rounded-full border-2 border-white"
                style={{ backgroundColor: '#EF4444' }}
              />
            </button>

            <div className="flex items-center gap-2.5 pl-3 ml-1" style={{ borderLeft: '1px solid #E2E8F0' }}>
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ring-2 ring-blue-100"
                style={{ backgroundColor: '#2563EB' }}
              >
                {initials}
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-semibold text-slate-800 leading-tight truncate max-w-[110px]">{fullName}</p>
                <p className="text-[10px] text-slate-400">Teacher</p>
              </div>
              <button
                onClick={logout}
                className="hidden sm:flex p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                data-testid="header-logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto" style={{ backgroundColor: '#F8FAFC' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
